import React, { Component } from "react"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import UserVisibleErrorStoringDecorator from "src/components/decorators/UserErrorStoringDecorator"
import SearchIcon from "src/components/search/SearchIcon"
import ApiHandler from "src/api/ApiHandler"
import navigationService from "src/navigation/NavigationService"

interface AreaSearchFormState {
    areaSearch: string
    suggestions: any[]
    showSuggestions: boolean
    loading: boolean
}

export default class AreaSearchForm extends Component<any, AreaSearchFormState> {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    private debounceTimeout: ReturnType<typeof setTimeout> | null = null

    constructor(props: any) {
        super(props)
        this.stateHandler = props.stateHandler
        this.apiHandler = props.apiHandler
        this.search = this.search.bind(this)
        this.handleInputChange = this.handleInputChange.bind(this)
        this.handleSuggestionClick = this.handleSuggestionClick.bind(this)
        this.handleInputBlur = this.handleInputBlur.bind(this)
        this.suggestAreas = this.suggestAreas.bind(this)

        this.state = {
            areaSearch: this.stateHandler.get(StateConstants.AREA_SEARCH_STATE) || "",
            suggestions: [],
            showSuggestions: false,
            loading: false
        }

        this.stateHandler.addListener(StateConstants.AREA_SEARCH_STATE, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                areaSearch: state ?? ""
            }))
        })
        this.stateHandler.addListener(StateConstants.AREA_SUGGESTIONS_STATE, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                suggestions: state ?? []
            }))
        })
        this.stateHandler.addListener(StateConstants.SHOW_AREA_SUGGESTIONS, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                showSuggestions: !!state
            }))
        })
        this.stateHandler.addListener(StateConstants.IS_LOADING, (state: any) => {
            this.setState((prevState) => ({
                ...prevState,
                loading: !!state
            }))
        })
    }

    @UserVisibleErrorStoringDecorator()
    async performSearch() {
        this.stateHandler.set(StateConstants.AREA_SUGGESTIONS_STATE, [])
        this.stateHandler.set(StateConstants.SHOW_AREA_SUGGESTIONS, false)

        const areaSearch = this.stateHandler.get(StateConstants.AREA_SEARCH_STATE)
        if (areaSearch === undefined || areaSearch === "") {
            return
        }

        const suggestions = this.stateHandler.get(StateConstants.AREA_SUGGESTIONS_STATE) || []
        if (suggestions.length > 0 && suggestions[0].id) {
            this.navigateToArea(suggestions[0].id)
            this.clearAreaField()
            return
        }

        const area = await this.apiHandler.findArea({ text: areaSearch })
        if (area?.id) {
            this.clearAreaField()
            this.navigateToArea(area.id)
        }
    }

    clearAreaField() {
        this.setState({ areaSearch: "", suggestions: [] })
        this.stateHandler.set(StateConstants.AREA_SEARCH_STATE, "")
        this.stateHandler.set(StateConstants.AREA_SUGGESTIONS_STATE, [])
        this.stateHandler.set(StateConstants.SHOW_AREA_SUGGESTIONS, false)
    }

    navigateToArea(areaId: string) {
        navigationService.navigateToArea(areaId)
    }

    @UserVisibleErrorStoringDecorator()
    async search(event: React.FormEvent) {
        event.preventDefault()
        void this.performSearch()
    }

    handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = event.target.value
        this.setState({ areaSearch: value })
        this.stateHandler.set(StateConstants.AREA_SEARCH_STATE, value)

        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout)
        }

        if (value.length >= 2) {
            this.setState({ loading: true })
            this.debounceTimeout = setTimeout(() => {
                void this.suggestAreas(value)
            }, 300)
        } else {
            this.setState({ suggestions: [], showSuggestions: false, loading: false })
            this.stateHandler.set(StateConstants.AREA_SUGGESTIONS_STATE, [])
            this.stateHandler.set(StateConstants.SHOW_AREA_SUGGESTIONS, false)
        }
    }

    async suggestAreas(searchTerm: string) {
        try {
            const response = await this.apiHandler.suggestArea({ area: searchTerm })
            const suggestions = response?.suggestions ?? []
            this.stateHandler.set(StateConstants.AREA_SUGGESTIONS_STATE, suggestions)
            this.stateHandler.set(StateConstants.SHOW_AREA_SUGGESTIONS, suggestions.length > 0)
        } catch (error) {
            console.error("Error suggesting areas:", error)
            this.stateHandler.set(StateConstants.AREA_SUGGESTIONS_STATE, [])
            this.stateHandler.set(StateConstants.SHOW_AREA_SUGGESTIONS, false)
        }
        this.setState({ loading: false })
    }

    handleSuggestionClick(suggestion: any) {
        const areaId = suggestion.id
        this.stateHandler.set(StateConstants.AREA_SUGGESTIONS_STATE, [])
        this.stateHandler.set(StateConstants.SHOW_AREA_SUGGESTIONS, false)
        if (areaId) {
            this.navigateToArea(areaId)
            this.clearAreaField()
        } else {
            this.stateHandler.set(StateConstants.AREA_SEARCH_STATE, suggestion.t || suggestion.text || "")
            void this.performSearch()
        }
    }

    handleInputBlur() {
        setTimeout(() => {
            this.stateHandler.set(StateConstants.SHOW_AREA_SUGGESTIONS, false)
        }, 200)
    }

    render() {
        const { areaSearch, suggestions, showSuggestions, loading } = this.state
        const borderClass = this.props.border ? "valuations" : ""

        return (
            <form className="search-form" onSubmit={this.search}>
                <fieldset className={"address-fieldset " + borderClass + " container"}>
                    <SearchIcon />
                    <div className="address-input-container">
                        <input
                            type="text"
                            className="field address-field"
                            placeholder="Search an area (e.g. Ranelagh, Dublin)"
                            id="area-search-input"
                            name="area"
                            value={areaSearch}
                            onChange={this.handleInputChange}
                            onBlur={this.handleInputBlur}
                            autoComplete="off"
                        />
                        {loading && (
                            <div className="loading-indicator"></div>
                        )}
                        {showSuggestions && suggestions.length > 0 && (
                            <div className="suggestions-dropdown">
                                {suggestions.map((suggestion, index) => (
                                    <div
                                        key={suggestion.id || index}
                                        className="suggestion-item"
                                        onMouseDown={() => this.handleSuggestionClick(suggestion)}
                                    >
                                        {suggestion.t ?? suggestion.text ?? suggestion.id}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </fieldset>
            </form>
        )
    }
}
